import { Button, CircularProgress, Divider, Fab, Grid, IconButton, Paper, Tooltip, Typography } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { values as getValues } from 'lodash';
import { withStyles, withTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import { showToast } from 'modules/layout/layout.actions';

import { ADD_COMMENT_FAILURE, DOWNLOAD_RELOSOURCE_ATTACHMENT_SUCCESS, GET_RELOSOURCE_COMMENTS_SUCCESS } from 'modules/intake/types';
import { AuthorizationComment } from 'modules/intake/models';
import { HEADER_HEIGHT } from 'modules/clients/constants';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { addCommentToRecord, downloadReloSourceAttachment, getReloSourceComments } from 'modules/intake/intake.actions';
import AddCommentDialog from './addCommentDialog.component';
import AuthorizationCommentView from './authorizationComment.component';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Box } from '@mui/material';

const styles = (theme) => ({
  noCommentsContainer: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  commentButtonContainer: {
    position: 'fixed',
    right: theme.spacing(1),
    top:HEADER_HEIGHT + 60,
    zIndex: 1500,
    paddingTop: theme.spacing(8),
  },
  reloSourceSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  notes: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    marginTop: theme.spacing(2),
  },
  attachment: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  container: {
    padding: theme.spacing(2),
  }
});

class AuthorizationCommentsForm extends PureComponent {
  state = {
    isAddingComment: false,
    isSubmitting: false,
    isLoadingReloSource: false,
    reloSourceError: false,
    reloSourceNotes: '',
    reloSourceAttachments: [],
    downloadingAttachmentIds: [],
  };

  componentDidMount() {
    this.isMountedForReloSource = true;
    this.loadReloSourceComments();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.values.tempFileId !== this.props.values.tempFileId) {
      this.loadReloSourceComments();
    }
  }

  componentWillUnmount() {
    this.isMountedForReloSource = false;
  }

  loadReloSourceComments = async () => {
    const { tempFileId } = this.props.values;
    const requestId = (this.reloSourceRequestId || 0) + 1;
    this.reloSourceRequestId = requestId;
    this.setState({
      isLoadingReloSource: Boolean(tempFileId),
      reloSourceError: false,
      reloSourceNotes: '',
      reloSourceAttachments: [],
    });
    if (!tempFileId) return;

    try {
      const action = await this.props.getReloSourceComments(tempFileId);
      if (!this.isMountedForReloSource || requestId !== this.reloSourceRequestId) return;
      if (action.type !== GET_RELOSOURCE_COMMENTS_SUCCESS) {
        this.setState({ isLoadingReloSource: false, reloSourceError: true });
        return;
      }
      this.setState({
        isLoadingReloSource: false,
        reloSourceNotes: action.response?.tempFile?.notes || '',
        reloSourceAttachments: action.response?.tempAttachments || [],
      });
    } catch (error) {
      if (this.isMountedForReloSource && requestId === this.reloSourceRequestId) {
        this.setState({ isLoadingReloSource: false, reloSourceError: true });
      }
    }
  };

  handleDownloadAttachment = async (attachment) => {
    const { attachmentId, fileName } = attachment;
    this.setState(({ downloadingAttachmentIds }) => ({
      downloadingAttachmentIds: [...downloadingAttachmentIds, attachmentId],
    }));
    try {
      const action = await this.props.downloadReloSourceAttachment(attachmentId);
      if (action.type !== DOWNLOAD_RELOSOURCE_ATTACHMENT_SUCCESS || !action.response) {
        throw new Error('Attachment download failed');
      }
      const objectUrl = window.URL.createObjectURL(action.response);
      const link = document.createElement('a');
      try {
        link.href = objectUrl;
        link.download = fileName || `attachment-${attachmentId}`;
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      this.props.showToast('Unable to download attachment.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } finally {
      if (this.isMountedForReloSource) {
        this.setState(({ downloadingAttachmentIds }) => ({
          downloadingAttachmentIds: downloadingAttachmentIds.filter((id) => id !== attachmentId),
        }));
      }
    }
  };

  handleCommentAdded = async (comment) => {
    const {
      values: { id, authorizationComments, authorizationInformation },
      metadata: { commentServiceTypes, commentRegardingTypes },
    } = this.props;
    this.setState({ isSubmitting: true });

    const commentWireData = comment.toWireData();
    const { type } = await this.props.addCommentToRecord(commentWireData, { 
      intakeRecordId: id,
      mtAuthorizationId: authorizationInformation?.mtAuthorizationId 
    });
    if (type === ADD_COMMENT_FAILURE) {
      this.props.showToast('Failed to save comment, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } else {
      this.props.setFieldValue('authorizationComments', [
        ...authorizationComments,
        new AuthorizationComment(comment, commentServiceTypes, commentRegardingTypes),
      ]);
      this.setState({ isAddingComment: false });
    }
    this.setState({ isSubmitting: false });
  };

  render() {
    const {
      classes,
      values: { authorizationComments },
      theme,
      metadata: { commentServiceTypes, commentRegardingTypes },
    } = this.props;
    const {
      isAddingComment, isSubmitting, isLoadingReloSource, reloSourceError,
      reloSourceNotes, reloSourceAttachments, downloadingAttachmentIds,
    } = this.state;
    return (
      <div style={{ position: 'relative' }}>
        <Grid container spacing={2}>
          {authorizationComments.map((comment, i) => (
            <Grid key={i} item xs={12}>
              <div className="mb-1">
                <AuthorizationCommentView comment={comment} />
              </div>
            </Grid>
          ))}
        </Grid>

        {authorizationComments.length === 0 && 
          <Paper className={classes.noCommentsContainer}>
            <Typography variant="h3">No Comments</Typography>
          </Paper>
        }

        <section className={classes.reloSourceSection}>
          <Divider />
        </section>

        <section className={classes.reloSourceSection} aria-labelledby="relosource-comments-heading">
          <Paper className={classes.container}>
            <Box sx={{ marginTop: 2 }}>
              <Typography id="relosource-comments-heading" variant="h5" className="mt-2">ReloSource Notes and Documents</Typography>
            </Box>
            {isLoadingReloSource ? (
              <CircularProgress size={24} className="mt-2" aria-label="Loading ReloSource comments" />
            ) : reloSourceError ? (
              <div role="alert" className="mt-2">
                <Typography color="error">Unable to load ReloSource comments.</Typography>
                <Button onClick={this.loadReloSourceComments} color="primary">Retry</Button>
              </div>
            ) : (
              <React.Fragment>
                {reloSourceNotes && <Typography className={classes.notes}>{reloSourceNotes}</Typography>}
                {reloSourceAttachments.map((attachment) => (
                  <div key={attachment.tempAttachmentId || attachment.attachmentId} className={classes.attachment}>
                    <Typography>{attachment.fileName || `attachment-${attachment.attachmentId}`}</Typography>
                    <Tooltip title="Download attachment">
                      <span>
                        <IconButton
                          color="primary"
                          aria-label={`Download ${attachment.fileName || 'attachment'}`}
                          disabled={!attachment.attachmentId || downloadingAttachmentIds.includes(attachment.attachmentId)}
                          onClick={() => this.handleDownloadAttachment(attachment)}
                        >
                          <GetAppIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                ))}
                {!reloSourceNotes && reloSourceAttachments.length === 0 && (
                  <Typography className="mt-2" color="textSecondary">No ReloSource comments or attachments.</Typography>
                )}
              </React.Fragment>
            )}
          </Paper>
        </section>

        <div className={classes.commentButtonContainer}>
          <Fab color="primary" onClick={() => this.setState({ isAddingComment: true })}>
            <FontAwesomeIcon size="lg" icon={['far', 'plus']} color={theme.palette.primary.contrastText} />
          </Fab>
        </div>
        {isAddingComment &&  // forces comment to clear each time, allows for retrying save if it fails
          <AddCommentDialog
            open={true}
            onDiscard={() => this.setState({ isAddingComment: false })}
            onSubmit={this.handleCommentAdded}
            serviceTypes={getValues(commentServiceTypes)}
            regardingTypes={getValues(commentRegardingTypes)}
            disabled={isSubmitting}
          />
        }
      </div>
    );
  }
}

AuthorizationCommentsForm.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  metadata: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  addCommentToRecord: PropTypes.func.isRequired,
  getReloSourceComments: PropTypes.func.isRequired,
  downloadReloSourceAttachment: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles),
  withTheme,
  connect(null, {
    addCommentToRecord,
    getReloSourceComments,
    downloadReloSourceAttachment,
    showToast,
  }),
)(AuthorizationCommentsForm);
